import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams  } from "react-router-dom";
import {
  ThemesPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
} from "./ThemesPage.styles";
import Sidebar from "../../../../components/Sidebar";
import Table from "../../../../components/Table";
import IconButton from "../../../../components/Buttons/IconButton";
import { Toast } from "primereact/toast";
import LessonsButton from "../../../../components/Buttons/LessonsButton";
import delIcon from "../../../../assets/icons/delIcon.svg";
import editIcon from "../../../../assets/icons/editIcon.svg";
import { ModuleTheme, ModuleSchema } from "../../../../services/documentService/documentService.types";
import {
  fetchModuleThemes,
  createModuleTheme,
  updateModuleTheme,
  deleteModuleTheme,
  fetchModuleThemeSchemas,
  getDocumentTemplate, // <-- ДОбавляем импорт схемы тем
} from "../../../../services/documentService/documentService";
import Button from "../../../../components/Buttons/Button";
import { ButtonWrapper } from "../../TemplatesPage.styles";
import { ConfirmModal } from "../../../../components/Modals";
import DeletedRow from "../../../../components/DeletedRow";
import RestoredRow from "../../../../components/RestoredRow";
import BreadcrumbLink from "../../../../components/BreadcrumbLink/BreadcrumbLink";

import ModuleModal from "../../../../components/Modals/ModuleModal/ModuleModal";
import { ButtonsContainer } from "../../../DocumentVersionsPage/DocumentVersionsPage.styles";

const ThemesPage: React.FC = () => {
  const navigate = useNavigate();
  const { moduleId, documentId } = useParams<{ moduleId: string, documentId: string }>();
  const [themes, setThemes] = useState<ModuleTheme[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(7);
  const [orderBy, setOrderBy] = useState<string>("");

  const [searchParams] = useSearchParams();

  const templateId = searchParams.get("templateId") || "0";
  const documentName = searchParams.get("documentName") || "Неизвестный документ";
  const templateName = searchParams.get("templateName") || "Неизвестный шаблон";
  const moduleName = searchParams.get("moduleName") || "Неизвестный модуль";


  const toast = useRef<Toast>(null);

  // Состояния для подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [themeToDelete, setThemeToDelete] = useState<ModuleTheme | null>(null);

  // Состояния для реализации отложенного удаления и уведомлений
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- Новые состояния для работы со схемой (по аналогии с ModulesPage) ---
  const [isThemeSchemaOpen, setIsThemeSchemaOpen] = useState<boolean>(false);
  const [moduleThemeSchemas, setModuleThemeSchemas] = useState<ModuleSchema[]>([]);
  const [editingTheme, setEditingTheme] = useState<ModuleTheme | null>(null);

  useEffect(() => {
    if (!moduleId) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Необходимо указать модуль.",
      });
      navigate("/login");
      return;
    }
    loadThemes();
  }, [moduleId, documentId, currentPage, pageSize, orderBy]);

  const loadThemes = async () => {
    if (!moduleId) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось получить ID модуля",
        life: 3000,
      });
      return;
    }
    setLoading(true);
    try {
      const response = await fetchModuleThemes(Number(moduleId), currentPage, pageSize, orderBy);
      setThemes(response.results);
      setTotalPages(Math.ceil(response.count / pageSize));
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Ошибка загрузки тем",
        life: 3000,
      });
      console.error("Ошибка при загрузке тем:", error);
    } finally {
      setLoading(false);
    }
  };


  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrderBy(`-${field}`);
    } else if (orderBy === `-${field}`) {
      setOrderBy("");
    } else {
      setOrderBy(field);
    }
  };

  // ---------- Удаление ----------
  const openDeleteModal = (theme: ModuleTheme) => {
    setThemeToDelete(theme);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setThemeToDelete(null);
  };

  const confirmDelete = () => {
    if (themeToDelete) {
      handleDelete(themeToDelete.id);
      closeDeleteModal();
    }
  };

  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteModuleTheme(id);
        setPendingDelete(null);
        toast.current?.show({
          severity: "success",
          summary: "Удалено",
          detail: "Тема успешно удалена",
          life: 2000,
        });
        loadThemes();
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить тему",
          life: 3000,
        });
        console.error("Ошибка при удалении темы:", error);
        setPendingDelete(null);
      }
    }, 5000);
  };

  const restoreThemeHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete;
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.current?.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление темы отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  // ---------- Открытие модалки для создания или редактирования ----------
  const openThemeSchemaModalForCreate = async () => {
    try {
      if (!documentId) return;
      const resp = await fetchModuleThemeSchemas(Number(templateId));
      setModuleThemeSchemas(resp ?? []);
      setEditingTheme(null);
      setIsThemeSchemaOpen(true);
    } catch (err) {
      console.error("Ошибка при загрузке схемы темы:", err);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить схему темы",
        life: 3000,
      });
    }
  };

  const openThemeSchemaModalForEdit = async (theme: ModuleTheme) => {
    try {
      if (!documentId) return;
      const resp = await fetchModuleThemeSchemas(Number(templateId));
      setModuleThemeSchemas(resp ?? []);
      setEditingTheme(theme);
      setIsThemeSchemaOpen(true);
    } catch (err) {
      console.error("Ошибка при загрузке схемы для редактирования темы:", err);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить схему темы",
        life: 3000,
      });
    }
  };

  // ---------- Логика сохранения (создание/редактирование) ----------
  const handleThemeSchemaSubmitCreate = async (formData: Record<string, string>) => {
    try {
      if (!documentId) return;


      const themeName = formData["title"] || "Без названия";
      const themePPnumber = formData["pp_number"] || "0";

      const createdTheme = await createModuleTheme(documentId, {
        name: themeName,
        pp_number: themePPnumber,
        module: moduleId,
        data: formData, // <-- Вся остальная информация сохраняется в data
      });

      toast.current?.show({
        severity: "success",
        summary: "Успех",
        detail: `Тема "${themeName}" создана`,
        life: 3000,
      });

      setRecentlyCreated(createdTheme.id);
      setIsThemeSchemaOpen(false);
      loadThemes();
      setTimeout(() => setRecentlyCreated(null), 3000);
    } catch (error) {
      console.error("Ошибка при создании темы:", error);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось создать тему",
        life: 3000,
      });
    }
  };

  const handleThemeSchemaSubmitEdit = async (formData: Record<string, string>) => {
    if (!editingTheme) return;
    try {
      const themeName = formData["title"] || editingTheme.name || "Без названия";
      const themePPnumber = formData["pp_number"] || editingTheme.pp_number || "0";

      await updateModuleTheme(editingTheme.id, {
        name: themeName,
        pp_number: themePPnumber,
        module: editingTheme.module,
        data: formData,
      });

      toast.current?.show({
        severity: "success",
        summary: "Успех",
        detail: `Тема "${themeName}" обновлена`,
        life: 3000,
      });

      setRecentlyEdited(editingTheme.id);
      setIsThemeSchemaOpen(false);
      loadThemes();
      setTimeout(() => setRecentlyEdited(null), 3000);
    } catch (error) {
      console.error("Ошибка при обновлении темы:", error);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось обновить тему",
        life: 3000,
      });
    }
  };

  // Вспомогательная функция для "ModuleModal" — начальные значения
  const getInitialValues = (): Record<string, string> => {
    if (!editingTheme || !editingTheme.data) return {};
    return editingTheme.data;
  };

  // Главная функция, которую передаём в модалку
  const handleThemeModalSubmit = (formData: Record<string, string>) => {
    if (!editingTheme) {
      handleThemeSchemaSubmitCreate(formData);
    } else {
      handleThemeSchemaSubmitEdit(formData);
    }
  };

  // ---------- Формирование таблицы ----------
  const headers = [
    { label: "Название темы", key: "name", fieldName: "name" },
    { label: "Порядковый номер темы", key: "pp_number", fieldName: "pp_number" },
    { label: "", key: "buttons", sortable: false, fieldName: "delete" },
  ];

  const rows = themes.map((theme) => {
    if (theme.id === pendingDelete) {
      return {
        name: (
          <DeletedRow
            id={theme.id}
            onRestore={restoreThemeHandler}
            colSpan={headers.length}
            message={`Тема "${theme.name}" будет удалена через 5 секунд.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    return {
      name: theme.name,
      pp_number: theme.pp_number,
      buttons: (
        <ButtonsContainer>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openThemeSchemaModalForEdit(theme)}
          />
          <IconButton
            icon={delIcon}
            altText="Удалить"
            onClick={() => openDeleteModal(theme)}
          />
        </ButtonsContainer>
      ),
    };
  });

  // Вставка уведомлений
  if (recentlyRestored !== null) {
    const restoredIndex = themes.findIndex((t) => t.id === recentlyRestored);
    if (restoredIndex !== -1) {
      rows.splice(restoredIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тема успешно восстановлена" />
        ),
      });
    }
  }
  if (recentlyCreated !== null) {
    const createdIndex = themes.findIndex((t) => t.id === recentlyCreated);
    if (createdIndex !== -1) {
      rows.splice(createdIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тема успешно создана" />
        ),
      });
    }
  }
  if (recentlyEdited !== null) {
    const editedIndex = themes.findIndex((t) => t.id === recentlyEdited);
    if (editedIndex !== -1) {
      rows.splice(editedIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тема успешно отредактирована" />
        ),
      });
    }
  }

  const rowAmountOptions = [
    { label: "7 записей", value: 7 },
    { label: "15 записей", value: 15 },
    { label: "20 записей", value: 20 },
  ];

  return (
    <ThemesPageContainer>
      <Toast ref={toast} />
      <Sidebar />
      <ContentWrapper>
        {/* Хлебные крошки */}
        <div style={{ marginBottom: "16px" }}>
          <BreadcrumbLink href="/documents" text="Шаблоны документов" />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/documents/${templateId}/versions?templateName=${templateName}`}
            text={`Шаблон ${templateName}`}
          />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/versions/${documentId}/modules?documentName=${documentName}&templateId=${templateId}&templateName=${templateName}`}
            text={`Модули документа ${documentName}`}
          />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink 
            href={`/modules/${documentId}/${moduleId}/themes?moduleName=${moduleName}&documentName=${documentName}&templateId=${templateId}}&templateName=${templateName}`}
            text={`Темы модуля ${moduleName}`} />
        </div>
        <HeaderContainer>
          <Title>Темы модуля</Title>
          <ButtonWrapper>
            {/* Вместо openCreateModal вызываем открытие схемы */}
            <Button onClick={openThemeSchemaModalForCreate} variant="active">
              Добавить тему
            </Button>
          </ButtonWrapper>
        </HeaderContainer>
        <Table
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
          minHeight="200px"
          onSort={handleSort}
          specialRowKey="name"
        />
      </ContentWrapper>

      {/* Окно подтверждения удаления */}
      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить тему "${themeToDelete?.name}"?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}

      {/* Модальное окно по схеме (создание/редактирование темы) */}
      {isThemeSchemaOpen && (
        <ModuleModal
          title={
            editingTheme
              ? `Редактирование темы "${editingTheme.name}"`
              : "Новая тема"
          }
          fields={moduleThemeSchemas ?? []}
          onSubmit={handleThemeModalSubmit}
          onCancel={() => setIsThemeSchemaOpen(false)}
          submitButtonText="Сохранить"
          initialValues={getInitialValues()}
        />
      )}
    </ThemesPageContainer>
  );
};

export default ThemesPage;
